import {call, delay, put, race, select, take} from 'redux-saga/effects';
import {layoutActions, layoutSelectors} from '..';
import {IStatesResponse} from 'types/StatesResponse';
import {isEmpty, isEqualWith} from 'lodash';
import {IDiplomatObject} from 'types/DiplomatObject';
import {pageActions, pageSelectors} from '../../CommonPage/redux';
import {DialogTypes} from 'common/names';
import i18n from 'i18n';
import {asyncSagaConstructor} from 'redux/common';

import loginSelectors from '../../../login/redux/selectors';

import types from '../types';
import {action} from 'redux/helpers';
import ApiService from '../../../../api/ApiService';
import api from '../../../../api';
import {licenseSelectors} from 'redux/license';
import {STATES_FULL_UPDATE_INTERVAL, STATES_UPDATE_INTERVAL} from 'common/constants';

export const getStatesFullSaga = asyncSagaConstructor(api.states, types.UPDATE_STATES_FULL, {
   getMethod: ApiService.getSecureJson,
});

function* getStatesSaga() {
   const sinceTime = (yield select(layoutSelectors.statesSince)) as number | undefined;
   const request = asyncSagaConstructor(
      sinceTime ? `${api.states}?since=${sinceTime}` : api.states,
      types.GET_STATES,
      {
         getMethod: ApiService.getSecureJson,
      }
   );

   yield call(request);
}

const comparator = (current: IStatesResponse['statesMap'], prev?: IStatesResponse['statesMap']) => {
   // console.debug(current, prev);
   const currentFolders = Object.values(current);
   if (!prev) return false;
   const currentCount = layoutSelectors.countItems(current);
   const prevCount = layoutSelectors.countItems(prev);
   // console.debug('comparator', currentCount, prevCount);
   if (currentCount !== prevCount) return false;

   for (let folder of currentFolders) {
      for (let p in folder) {
         if (folder.hasOwnProperty(p)) {
            const diplomatObject = folder[p].diplomatObject;
            const prevDiplomatObj = layoutSelectors.getItem(
               prev,
               diplomatObject.folderId,
               diplomatObject.name
            )?.diplomatObject;
            if (!prevDiplomatObj || diplomatObject.timestamp !== prevDiplomatObj.timestamp)
               return false;
         }
      }
   }
   // for (let {diplomatObject} of currentValues) {
   //    const name = diplomatObject.name;
   //    const prevDiplomatObj = prev[name]?.diplomatObject;
   //    if (!prevDiplomatObj || diplomatObject.timestamp !== prevDiplomatObj.timestamp) return false;
   // }

   return true;
};

function* checkOpenedObject(states: IStatesResponse) {
   const data: IDiplomatObject = (yield select(pageSelectors.getData)) as ReturnType<
      typeof pageSelectors.getData
   >;
   const diplomatObject = layoutSelectors.getItem(states.statesMap, data.folderId, data.name)
      ?.diplomatObject;
   if (
      !isEmpty(data) &&
      (!diplomatObject || diplomatObject.timestamp !== data.timestamp) &&
      diplomatObject.folderId === data.folderId
   ) {
      yield put(
         layoutActions.openSubDialog({
            id: DialogTypes.AlertDialog.id,
            title: i18n.t('Active Panel Updated'),
            content: i18n.t(
               'The object currently displayed has been updated by another user.  ' +
                  'Your screen reflects the current content\n' +
                  'of the object.  Refer to the bottom of your screen for the ' +
                  'Domain/User ID that performed the update.'
            ),
         })
      );
      yield put(pageActions.getData(data.id, data.objectType!));
   }
}

function* compareStateResult(prevStates?: IStatesResponse, isSkipStatesLoading?: boolean) {
   const states: IStatesResponse = (yield select(layoutSelectors.statesSelector)) as ReturnType<
      typeof layoutSelectors.statesSelector
   >;
   if (!isEqualWith(states.statesMap, prevStates?.statesMap, comparator)) {
      yield put(action(types.LOAD_TREE_NAV.SAGA, {isSkipStatesLoading}));
      yield call(checkOpenedObject, states);
   }
}

export function* updateStatesSaga() {
   while (true) {
      try {
         const {skip} = yield race({
            tiemout: delay(STATES_UPDATE_INTERVAL),
            skip: take(types.UPDATE_STATES_FULL.SUCCESS), // if it is called from loadTreeNav, or start timer only after getting result
         });
         if (skip) continue;

         const isAuthenticated = (yield select(loginSelectors.isAuthenticated)) as ReturnType<
            typeof loginSelectors.isAuthenticated
         >;
         const license = (yield select(licenseSelectors.getLicense)) as ReturnType<
            typeof licenseSelectors.getLicense
         >;

         //TODO update license
         if (isAuthenticated && license) {
            const prevStates = (yield select(layoutSelectors.statesSelector)) as ReturnType<
               typeof layoutSelectors.statesSelector
            >;
            yield call(getStatesSaga);
            yield call(compareStateResult, prevStates);
         } else {
            //TODO notify about license or trigger getting
            // yield put()
         }
      } catch (e) {
         console.error('updateStatesSaga', e);
      }
   }
}

export function* updateFullStatesSaga() {
   while (true) {
      try {
         const {skip} = yield race({
            immediate: take(types.UPDATE_STATES_FULL.SAGA),
            tiemout: delay(STATES_FULL_UPDATE_INTERVAL),
            skip: take(types.UPDATE_STATES_FULL.SUCCESS), // if it is called from loadTreeNav, or start timer only after getting result
         });
         if (skip) continue;

         const isAuthenticated = (yield select(loginSelectors.isAuthenticated)) as ReturnType<
            typeof loginSelectors.isAuthenticated
         >;
         const license = (yield select(licenseSelectors.getLicense)) as ReturnType<
            typeof licenseSelectors.getLicense
         >;
         if (!(isAuthenticated && license)) continue;

         const prevStates = (yield select(layoutSelectors.statesSelector)) as ReturnType<
            typeof layoutSelectors.statesSelector
         >;
         yield call(getStatesFullSaga);
         yield call(compareStateResult, prevStates, true);
      } catch (e) {
         console.error('updateFullStatesSaga', e);
      }
   }
}
